import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { UserRoleType } from '../../config/roles';
import { useTranslation } from '../../i18n';
import { adminToolsKeys } from './adminTools.queries';
import type { CreateDroneRequest, OwnershipType, UpdateLegacyHoursRequest } from '../../types/api';

// STD-015: write/imperative actions for the AdminTools domain. apiService is
// imported here (a *.mutations.ts file) per STD-016. Errors are toasted by the
// global MutationCache handler; meta.errorMessage is the fallback.

// Create an invitation for a new user with the given email and roles.
export function useCreateInvitationMutation() {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('adminToolsPage.inviteUser.failedToSend') },
    mutationFn: async ({ email, roles }: { email: string; roles: UserRoleType[] }) => {
      return apiService.createInvitation(email, roles);
    },
  });
}

// Assign an unassigned user to an organization with the given roles.
export function useAssignUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      orgId,
      roles,
    }: {
      userId: string;
      orgId: string;
      roles: UserRoleType[];
    }) => {
      return apiService.assignUserToOrganization(userId, orgId, roles);
    },
    onSuccess: (_data, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.organizationUsers(orgId) });
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.unassignedUsers() });
    },
  });
}

// Create a new organization with the given name.
export function useCreateOrganizationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      return apiService.createOrganization(name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.organizations() });
    },
  });
}

// Send a new announcement with optional file attachments.
export function useCreateAnnouncementMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      title,
      body,
      targetType,
      targetIds,
      files,
      sendEmailNotification,
    }: {
      title: string;
      body: string;
      targetType: 'all_users' | 'organizations' | 'users';
      targetIds: string[];
      files: File[];
      sendEmailNotification: boolean;
    }) => {
      return apiService.createAnnouncementWithAttachments(
        title,
        body,
        targetType,
        targetIds,
        files,
        sendEmailNotification
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.announcementsAdmin() });
    },
  });
}

// Start a background metadata extraction job.
export function useStartExtractionMutation() {
  return useMutation({
    mutationFn: async (globalScope: boolean) => {
      return apiService.startMetadataExtraction(globalScope);
    },
  });
}

// Download the log file for a completed extraction job as a Blob.
export function useDownloadExtractionLogMutation() {
  return useMutation({
    mutationFn: async (jobId: string) => {
      return apiService.downloadExtractionLog(jobId);
    },
  });
}

// Create a new drone. The consuming hook renders its own success/error toasts and
// inline result UI, so the global error toast is skipped here.
export function useCreateDroneMutation() {
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (droneData: CreateDroneRequest) => {
      return apiService.createDrone(droneData);
    },
  });
}

// Backfill file hashes for existing flights (dry-run or live).
// The consuming hook renders its own toasts and inline result UI.
export function useBackfillFileHashesMutation() {
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (dryRun: boolean) => {
      return apiService.backfillFileHashes(dryRun);
    },
  });
}

// Check for duplicate file hashes. The consuming hook renders its own toasts.
export function useCheckDuplicateHashesMutation() {
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async () => {
      return apiService.checkDuplicateHashes();
    },
  });
}

// Audit S3 consistency to find orphaned DB records. Hook renders its own toasts.
export function useAuditS3ConsistencyMutation() {
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async () => {
      return apiService.auditS3Consistency();
    },
  });
}

// Delete orphaned DB records (where the S3 file is missing). Hook renders its own toasts.
export function useDeleteOrphanedRecordsMutation() {
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (flightIds: string[]) => {
      return apiService.deleteOrphanedRecords(flightIds);
    },
  });
}

// Update a drone's legacy hours. Invalidates the drones-legacy-hours list on success.
export function useUpdateDroneLegacyHoursMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({
      serialNumber,
      hours,
    }: {
      serialNumber: string;
      hours: UpdateLegacyHoursRequest;
    }) => {
      return apiService.updateDroneLegacyHours(serialNumber, hours);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.dronesLegacyHours() });
    },
  });
}

// Update a pilot's legacy flight hours. Invalidates the pilots-legacy-hours list on success.
export function useUpdatePilotLegacyHoursMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({ userId, hours }: { userId: string; hours: UpdateLegacyHoursRequest }) => {
      return apiService.updatePilotLegacyHours(userId, hours);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.pilotsLegacyHours() });
    },
  });
}

// Upload a maintenance document (PDF). Invalidates the document list on success.
export function useUploadMaintenanceDocumentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({
      file,
      documentTitle,
      documentType,
      documentVersion,
      droneModel,
      manufacturer,
    }: {
      file: File;
      documentTitle: string;
      documentType: string;
      documentVersion?: string;
      droneModel?: string;
      manufacturer?: string;
    }) => {
      return apiService.uploadMaintenanceDocument(
        file,
        documentTitle,
        documentType,
        documentVersion,
        droneModel,
        manufacturer
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.maintenanceDocuments() });
    },
  });
}

// Delete a maintenance document by S3 key. Invalidates the document list on success.
export function useDeleteMaintenanceDocumentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (s3Key: string) => {
      return apiService.deleteMaintenanceDocument(s3Key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.maintenanceDocuments() });
    },
  });
}

// Process (chunk + embed) a maintenance document by S3 key. Invalidates the list on success.
export function useProcessMaintenanceDocumentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (s3Key: string) => {
      return apiService.processMaintenanceDocument(s3Key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.maintenanceDocuments() });
    },
  });
}

// Update a drone's organization assignment (XER admin only).
// Invalidates the drones-with-organizations list on success.
export function useUpdateDroneOrganizationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({
      serialNumber,
      organizationId,
    }: {
      serialNumber: string;
      organizationId: string;
    }) => {
      return apiService.updateDroneOrganization(serialNumber, organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.dronesWithOrganizations() });
    },
  });
}

// Update a drone's ownership information (XER admin only).
// Invalidates the drones-with-organizations list on success.
export function useUpdateDroneOwnershipMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({
      serialNumber,
      ownershipType,
      leaseEndDate,
    }: {
      serialNumber: string;
      ownershipType: OwnershipType | null;
      leaseEndDate: string | null;
    }) => {
      return apiService.updateDroneOwnership(serialNumber, ownershipType, leaseEndDate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.dronesWithOrganizations() });
    },
  });
}

// Update a user's roles. Invalidates the caller's organization users list on success.
export function useUpdateUserRolesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({ userId, roles }: { userId: string; roles: UserRoleType[] }) => {
      return apiService.updateUserRoles(userId, roles);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminToolsKeys.myOrganizationUsers() });
    },
  });
}

// Recalculate maintenance counters for a drone. Hook renders its own toasts/inline result.
export function useRecalculateMaintenanceCountersMutation() {
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (droneSerial: string) => {
      return apiService.recalculateMaintenanceCounters(droneSerial);
    },
  });
}

// Re-queue a tender email for reprocessing. Hook renders its own toast.
export function useReprocessTenderEmailMutation() {
  return useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async (emailId: string) => {
      return apiService.reprocessTenderEmail(emailId);
    },
  });
}
